import useTranslation from 'next-translate/useTranslation';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { showNotification } from '@mantine/notifications';

import { mailToSupport } from '~/components/utils/mailToSupport';
import { likePost, unlikePost } from '~/services/commissioner/commissioner-service.service.api';

export function usePostActions({ id }: { id: string }) {
  const { t } = useTranslation('commissioner');
  const queryClient = useQueryClient();

  const { mutate: handleLikeClick, isLoading: isLoadingLike } = useMutation({
    mutationFn: async (postId?: string) => likePost(postId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['commissioner/posts'] });
    },
    onError: (error: AxiosError<{ message?: string }>) => {
      const errorMessage = error?.response?.data?.message;
      showNotification({
        title: t('hub.posts.postLikeError'),
        message: errorMessage,
        color: 'red',
        autoClose: 5000,
      });
    },
  });

  const { mutate: handleUnlikeClick, isLoading: isLoadingUnlike } = useMutation({
    mutationFn: async (postId?: string) => unlikePost(postId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['commissioner/posts'] });
    },
    onError: (error: AxiosError<{ message?: string }>) => {
      const errorMessage = error?.response?.data?.message;
      showNotification({
        title: t('hub.posts.postUnlikeError'),
        message: errorMessage,
        color: 'red',
        autoClose: 5000,
      });
    },
  });

  const handleReportPost = () => {
    const message = `I would like to report a post. [Post ID: ${id}]`;
    mailToSupport({
      subject: 'Report a Post',
      body: message,
    });
  };

  return { handleLikeClick, isLoadingLike, handleUnlikeClick, isLoadingUnlike, handleReportPost };
}
